import React, { useState, useEffect } from 'react';
import { useConsent } from '../../context/ConsentContext';
import { getConsentDetail } from '../../utils/consent';

export default function CookiePreferencesModal({ onClose }) {
  const { saveConsent } = useConsent();
  const [preferences, setPreferences] = useState(() => getConsentDetail());

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    const allAccepted = preferences.statistik && preferences.marketing;
    saveConsent(allAccepted ? 'all' : 'essential', preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allPrefs = { statistik: true, marketing: true };
    saveConsent('all', allPrefs);
    onClose();
  };

  const handleEssentialOnly = () => {
    const essentialPrefs = { statistik: false, marketing: false };
    saveConsent('essential', essentialPrefs);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4"
      style={{ background: 'rgba(0, 0, 0, 0.98)', backdropFilter: 'blur(24px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-black border-2 border-white/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl max-h-[90vh] overflow-y-auto glow-green-soft"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Cookie Icon */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 md:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center text-xl sm:text-2xl glow-green-soft">
              🍪
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Cookie-Einstellungen</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg sm:rounded-xl border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all flex-shrink-0"
            aria-label="Schließen"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Kategorien */}
        <div className="space-y-3 sm:space-y-4 md:space-y-5 mb-4 sm:mb-6 md:mb-8">
          {/* Notwendig (immer aktiv) */}
          <div className="p-3 sm:p-4 md:p-5 border-2 border-[#10B981]/30 rounded-lg sm:rounded-xl bg-[#10B981]/5 glow-green-soft">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h3 className="font-bold text-white text-base sm:text-lg">✓ Notwendig</h3>
              <div className="w-12 h-6 sm:w-14 sm:h-7 bg-[#10B981] rounded-full flex items-center px-1 sm:px-1.5 glow-green-soft flex-shrink-0">
                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-black rounded-full translate-x-5 sm:translate-x-6"></div>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
              Diese Cookies sind erforderlich, damit die Website funktioniert (Login, Session, etc.). Sie können nicht deaktiviert werden.
            </p>
          </div>

          {/* Statistik */}
          <div className="p-3 sm:p-4 md:p-5 border-2 border-white/20 rounded-lg sm:rounded-xl bg-white/5 hover:border-white/30 transition-all">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h3 className="font-bold text-white text-base sm:text-lg">📊 Statistik</h3>
              <button
                onClick={() => handleToggle('statistik')}
                className={`w-12 h-6 sm:w-14 sm:h-7 rounded-full flex items-center px-1 sm:px-1.5 transition-all flex-shrink-0 ${
                  preferences.statistik
                    ? 'bg-[#10B981] glow-green-soft'
                    : 'bg-white/10 border-2 border-white/30'
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full transition-all ${
                    preferences.statistik ? 'bg-black translate-x-5 sm:translate-x-6' : 'bg-white/60'
                  }`}
                ></div>
              </button>
            </div>
            <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
              Hilft uns zu verstehen, wie Besucher die Seite nutzen (anonymisiert). Keine personenbezogenen Daten.
            </p>
          </div>

          {/* Marketing */}
          <div className="p-3 sm:p-4 md:p-5 border-2 border-white/20 rounded-lg sm:rounded-xl bg-white/5 hover:border-white/30 transition-all">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h3 className="font-bold text-white text-base sm:text-lg">🎯 Marketing</h3>
              <button
                onClick={() => handleToggle('marketing')}
                className={`w-12 h-6 sm:w-14 sm:h-7 rounded-full flex items-center px-1 sm:px-1.5 transition-all flex-shrink-0 ${
                  preferences.marketing
                    ? 'bg-[#10B981] glow-green-soft'
                    : 'bg-white/10 border-2 border-white/30'
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full transition-all ${
                    preferences.marketing ? 'bg-black translate-x-5 sm:translate-x-6' : 'bg-white/60'
                  }`}
                ></div>
              </button>
            </div>
            <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
              Ermöglicht personalisierte Inhalte und Werbung basierend auf deinen Interessen.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
          <button
            onClick={handleEssentialOnly}
            className="flex-1 py-3 sm:py-3.5 md:py-4 px-4 sm:px-5 md:px-6 border-2 border-white/30 text-white rounded-lg sm:rounded-xl hover:bg-white/5 hover:border-white/50 transition-all font-bold text-sm sm:text-base"
          >
            Nur Notwendige
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-3 sm:py-3.5 md:py-4 px-4 sm:px-5 md:px-6 border-2 border-white/50 text-white rounded-lg sm:rounded-xl hover:bg-white/10 hover:border-white transition-all font-bold text-sm sm:text-base"
          >
            Auswahl speichern
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 py-3 sm:py-3.5 md:py-4 px-4 sm:px-5 md:px-6 bg-[#10B981] text-black rounded-lg sm:rounded-xl hover:bg-[#059669] transition-all font-black text-sm sm:text-base glow-green pulse-glow shine-effect"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}